import { useState } from 'react';
import ModalShell from './ModalShell';
import Button from './Button';
import { Icon } from './Icon';

interface SecretRevealProps {
  isOpen: boolean;
  onClose: () => void;
  /** The plaintext secret to display once. Never persisted. */
  value: string;
  /** Modal title. Default: "Token creado". */
  title?: string;
  /** Optional supporting paragraph above the value. */
  description?: string;
  /** Label for the warning shown below the value. */
  warning?: string;
  /** Label for the copy button (idle). Default Spanish ("Copiar"). */
  copyLabel?: string;
  /** Label for the copy button after a successful copy. Default Spanish
   *  ("Copiado"). */
  copiedLabel?: string;
}

/**
 * Reveal a freshly-minted secret (API token, webhook secret, etc.) ONCE
 * with a copy-to-clipboard button and a clear "save it now" warning.
 *
 * The component does NOT persist or transmit the value — it just
 * renders what the parent passes in. The parent must clear the value
 * from its state on close (we cannot enforce that here).
 *
 * Standard component for any "shown only on creation" secret across
 * admin and tenant — import from `@shared`.
 *
 * @example
 * <SecretReveal
 *   isOpen={!!plainToken}
 *   onClose={() => setPlainToken(null)}
 *   value={plainToken ?? ''}
 *   title="Token creado"
 *   description="Cópialo ahora — no podrás volver a verlo."
 * />
 */
export default function SecretReveal({
  isOpen,
  onClose,
  value,
  title = 'Token creado',
  description = 'Copia este valor ahora. Por seguridad no podrás volver a verlo después de cerrar este diálogo.',
  warning = 'Si lo pierdes, deberás revocar este token y crear uno nuevo.',
  copyLabel = 'Copiar',
  copiedLabel = 'Copiado',
}: SecretRevealProps) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      // Short visual confirmation; resets after 2s in case the user reopens
      // a different secret in the same session.
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard API can fail in non-secure contexts — fall back to
      // text selection so the user can still copy manually.
      const sel = window.getSelection();
      const node = document.getElementById('secret-reveal-value');
      if (sel && node) {
        const range = document.createRange();
        range.selectNodeContents(node);
        sel.removeAllRanges();
        sel.addRange(range);
      }
    }
  };

  const handleClose = () => {
    setCopied(false);
    onClose();
  };

  return (
    <ModalShell
      isOpen={isOpen}
      onClose={handleClose}
      title={title}
      maxWidth="max-w-lg"
      size="auto"
    >
      <div className="space-y-4">
        {description && (
          <p className="text-sm text-text-main dark:text-slate-300 leading-relaxed">
            {description}
          </p>
        )}

        <div className="flex items-stretch gap-2">
          <code
            id="secret-reveal-value"
            className="flex-1 min-w-0 rounded-lg bg-slate-100 dark:bg-surface-hover-dark px-3 py-2.5 text-xs font-mono text-text-main dark:text-slate-200 break-all select-all"
          >
            {value}
          </code>
          <Button
            variant={copied ? 'primary' : 'secondary'}
            onClick={handleCopy}
            icon={copied ? 'check' : 'content_copy'}
          >
            {copied ? copiedLabel : copyLabel}
          </Button>
        </div>

        <div className="flex items-start gap-2 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 px-3 py-2.5">
          <Icon name="warning" className="!text-[18px] text-amber-600 dark:text-amber-400 mt-0.5" />
          <p className="text-xs text-amber-800 dark:text-amber-200 leading-relaxed">
            {warning}
          </p>
        </div>
      </div>
    </ModalShell>
  );
}
